import { styled, css } from 'css-literal-loader/styled';
import { navigate } from 'gatsby';
import React from 'react';
import { filterAndMapMessages } from 'react-formal/lib/utils/ErrorUtils';
import { FormattedMessage, injectIntl } from 'react-intl';
import Layout from '@4c/layout';

import { CONTACT_URL } from '../config';
import paths from '../paths';
import configureTeam from '../schema/configureTeam';
import withReservation from '../utils/Reservation';
import DropdownList from './DropdownList';
import TooltipDropdownField from './TooltipDropdownField';
import Form from './Form';
import FormattedPrice from './FormattedPrice';
import {
  ButterflyIqLightningFootnote,
  ButterflyIqUsbcFootnote,
} from './PriceFootnotes';
import ProbeImageSegment, { MARGIN } from './ProbeImageSegment';
import Responsive from './Responsive';
import Text from './Text';
import SubscriptionInfoLink from './SubscriptionInfoLink';
import ValidationTooltip from './ValidationTooltip';
import WarrantyInfoLink from './WarrantyInfoLink';
import CompatibilityInfoLink from './CompatibilityInfoLink';

const rangeFromZero = num => Array.from(new Array(num + 1), (_, i) => i);

const {
  Button,
  Heading,
  Price,
  Section,
  DeviceLightningName,
  DeviceUsbcName,
  LicensedUsers,
  MadeForIPhoneIPadBadge,
} = ProbeImageSegment;

export const QuantityDropdownList = styled(DropdownList, null, "QuantityDropdownList", require("./ConfigureTeamPageSegment-QuantityDropdownList.module.scss"), "quantity-dropdown-list", "quantityDropdownList");

const styles = require('./ConfigureTeamPageSegment-styles.module.scss');

const propTypes = {};

class ConfigureTeamPageSegment extends React.Component {
  state = {};

  static getDerivedStateFromProps({ cart }) {
    const { maxCloudUsers, minCloudUsers } = cart;
    const { maxDevices } = cart.remote.inventory;

    return {
      numCloudUsersList: rangeFromZero(maxCloudUsers)
        .slice(minCloudUsers)
        .map(value => ({
          value,
          text: `${value}`,
        }))
        .concat({
          value: maxCloudUsers + 1,
          text: `${maxCloudUsers + 1}+`,
        }),
      numDevicesLightningList: rangeFromZero(maxDevices)
        .map(value => ({
          value,
          text: value,
        }))
        .concat({
          value: maxDevices + 1,
          text: `${maxDevices + 1}+`,
        }),
      numDevicesUsbCList: rangeFromZero(maxDevices)
        .map(value => ({
          value,
          text: value,
        }))
        .concat({
          value: maxDevices + 1,
          text: `${maxDevices + 1}+`,
        }),
    };
  }

  handleChange = (value, changedPaths) => {
    // Behavior: We are concerned with the error from the most recent change
    // If a device field was changed, we show error from that device and users
    this.setState(({ errors }) => {
      const nextErrors = { ...errors };

      if (changedPaths.includes('numCloudUsers')) {
        delete nextErrors.numDevicesLightning;
        delete nextErrors.numDevicesUsbC;
      }
      if (changedPaths.includes('numDevicesLightning')) {
        delete nextErrors.numDevicesUsbC;
      }
      if (changedPaths.includes('numDevicesUsbC')) {
        delete nextErrors.numDevicesLightning;
      }

      return { value, errors: nextErrors };
    });
  };

  handleErrors = errors => {
    // Behavior: Tooltip should show on the changed field
    // and if device field was changed, also show on users
    this.setState(_ => {
      return { errors };
    });
  };

  handleSubmit = async ({
    numCloudUsers,
    numDevicesLightning,
    numDevicesUsbC,
  }) => {
    const { cart } = this.props;

    await cart.setQuantities(
      numCloudUsers,
      numDevicesLightning,
      numDevicesUsbC,
    );

    navigate(paths.cart);
  };

  buildQuantityField(name, validates, fieldProps) {
    const ref = React.createRef();
    return (
      <Form.Field {...fieldProps}>
        {({ meta, ...props }) => {
          const errors = filterAndMapMessages({
            messages: meta.errors,
            names: validates,
          });
          const invalid = !!Object.keys(errors).length;
          const nextMeta = { ...meta, errors, invalid, valid: !invalid };
          return (
            <>
              <QuantityDropdownList {...props} ref={ref} meta={nextMeta} />
              <ValidationTooltip
                theme="danger"
                target={ref.current}
                show={invalid}
                errors={errors}
              />
            </>
          );
        }}
      </Form.Field>
    );
  }

  render() {
    const { cart } = this.props;
    const {
      maxCloudUsers,
      minCloudUsers,
      cloudTeamBasePricePerMonth,
      cloudTeamAdditionalPricePerMonth,
    } = cart;
    const { devicePrice, maxDevices } = cart.remote.inventory;
    const {
      numCloudUsersList,
      numDevicesLightningList,
      numDevicesUsbCList,
      value,
      errors,
    } = this.state;
    const mustContactUs =
      value &&
      (value.numCloudUsers > maxCloudUsers ||
        value.numDevicesLightning + value.numDevicesUsbC > maxDevices);

    return (
      <Responsive smDown>
        {isMobile => (
          <ProbeImageSegment showImage={!isMobile}>
            <Form
              key="step-2"
              value={value}
              errors={errors}
              schema={configureTeam}
              onError={this.handleErrors}
              onChange={this.handleChange}
              onSubmit={this.handleSubmit}
              context={{ maxCloudUsers, maxDevices }}
              defaultValue={{
                numCloudUsers: cart.numCloudUsers,
                numDevicesLightning: cart.numDevicesLightning,
                numDevicesUsbC: cart.numDevicesUsbC,
              }}
            >
              {!isMobile && (
                <span className={styles.madeForIphone}>
                  <MadeForIPhoneIPadBadge />
                </span>
              )}
              <Heading
                wrap
                display="flex"
                justify="space-between"
                align="center"
              >
                <FormattedMessage
                  id="configure.team.users.title"
                  defaultMessage="Team Subscription"
                />
                {!isMobile && <SubscriptionInfoLink />}
              </Heading>

              <Text level={isMobile ? '3' : '2'}>
                <FormattedMessage
                  id="configure.team.users"
                  defaultMessage="Scan, store and collaborate with unlimited software upgrades and HIPAA compliant cloud storage."
                />
              </Text>
              {
                <>
                  <Section
                    pad={2}
                    display="flex"
                    align={isMobile ? 'stretch' : 'flex-start'}
                    direction={isMobile ? 'column' : 'row'}
                  >
                    {isMobile ? (
                      <>
                        <Layout
                          direction="row"
                          justify="space-between"
                          align="flex-end"
                        >
                          <Text level="2">
                            <SubscriptionInfoLink />
                          </Text>
                          <MadeForIPhoneIPadBadge />
                        </Layout>
                        <Layout direction="row" justify="space-between">
                          <Text level="2">
                            <LicensedUsers />
                          </Text>
                          <Text level="2">
                            <Price
                              price={cloudTeamBasePricePerMonth * 12}
                              perYear
                            />
                          </Text>
                        </Layout>
                        <Text
                          muted
                          alignLeft
                          level="3"
                          className={styles.mobileFootnote}
                        >
                          <FormattedMessage
                            id="configure.team.users.pricing"
                            defaultMessage="includes {minCloudUsers} users, {cloudTeamAdditionalPricePerYear} per additional user, billed annually"
                            values={{
                              minCloudUsers,
                              cloudTeamAdditionalPricePerYear: (
                                <FormattedPrice
                                  value={cloudTeamAdditionalPricePerMonth * 12}
                                  perYear
                                />
                              ),
                            }}
                          />
                        </Text>
                      </>
                    ) : (
                      <>
                        <Layout
                          direction="column"
                          align="flex-start"
                          shrink
                          className={styles.priceColumn}
                        >
                          <Text level="1">
                            <Price
                              price={cloudTeamBasePricePerMonth * 12}
                              perYear
                            />
                          </Text>
                          <Text muted alignLeft level="3">
                            <FormattedMessage
                              id="configure.team.users.pricing"
                              defaultMessage="includes {minCloudUsers} users, {cloudTeamAdditionalPricePerYear} per additional user, billed annually"
                              values={{
                                minCloudUsers,
                                cloudTeamAdditionalPricePerYear: (
                                  <FormattedPrice
                                    value={
                                      cloudTeamAdditionalPricePerMonth * 12
                                    }
                                    perYear
                                  />
                                ),
                              }}
                            />
                          </Text>
                        </Layout>
                        <Layout grow>
                          <Text level="1">
                            <LicensedUsers />
                          </Text>
                        </Layout>
                      </>
                    )}
                    <div>
                      <TooltipDropdownField
                        invalid={false}
                        name="numCloudUsers"
                        validates={['numCloudUsers']}
                        textField="text"
                        valueField="value"
                        mapFromValue={v => v.value}
                        data={numCloudUsersList}
                        as={QuantityDropdownList}
                        isSmall={isMobile}
                      />
                    </div>
                  </Section>

                  <Heading
                    bordered
                    wrap
                    display="flex"
                    justify="space-between"
                    align="center"
                  >
                    <FormattedMessage
                      id="configure.team.devices.title"
                      defaultMessage="Butterfly iQ"
                    />
                    <WarrantyInfoLink />
                  </Heading>
                  <Text level={isMobile ? '3' : '2'}>
                    <FormattedMessage
                      id="configure.team.devices"
                      defaultMessage="Choose the iQ for your iPhone or iPad."
                    />
                  </Text>
                  <div>
                    <CompatibilityInfoLink
                      compatibilitySegmentRef={
                        this.props.compatibilitySegmentRef
                      }
                    />
                  </div>
                  <Section
                    pad={2}
                    display="flex"
                    align={isMobile ? 'stretch' : 'flex-start'}
                    direction={isMobile ? 'column' : 'row'}
                  >
                    {isMobile ? (
                      <>
                        <Layout
                          direction="row"
                          align="flex-start"
                          justify="space-between"
                          grow
                        >
                          <Text level="2">
                            <DeviceLightningName />
                          </Text>
                          <Text level="2">
                            <Price price={devicePrice} noCents />
                          </Text>
                        </Layout>
                        <ButterflyIqLightningFootnote
                          alignLeft
                          className={styles.mobileFootnote}
                        />
                      </>
                    ) : (
                      <>
                        <Layout
                          direction="column"
                          align="flex-start"
                          justify="flex-start"
                          shrink
                          className={styles.priceColumn}
                        >
                          <Text level="1">
                            <Price price={devicePrice} noCents />
                          </Text>
                        </Layout>
                        <Layout
                          direction="column"
                          align="flex-start"
                          justify="flex-start"
                          grow
                        >
                          <Text level="1">
                            <DeviceLightningName />
                          </Text>
                          <ButterflyIqLightningFootnote alignLeft />
                        </Layout>
                      </>
                    )}
                    <div>
                      <TooltipDropdownField
                        invalid={false}
                        name="numDevicesLightning"
                        events={['onChange']}
                        validates={['numDevicesLightning', 'numCloudUsers']}
                        textField="text"
                        valueField="value"
                        mapFromValue={v => v.value}
                        data={numDevicesLightningList}
                        as={QuantityDropdownList}
                        isSmall={isMobile}
                      />
                    </div>
                  </Section>
                  <Section
                    pad={2}
                    display="flex"
                    align={isMobile ? 'stretch' : 'flex-start'}
                    direction={isMobile ? 'column' : 'row'}
                  >
                    {isMobile ? (
                      <>
                        <Layout
                          direction="row"
                          align="flex-start"
                          justify="space-between"
                          grow
                        >
                          <Text level="2">
                            <DeviceUsbcName />
                          </Text>
                          <Text level="2">
                            <Price price={devicePrice} noCents />
                          </Text>
                        </Layout>
                        <ButterflyIqUsbcFootnote
                          alignLeft
                          className={styles.mobileFootnote}
                        />
                      </>
                    ) : (
                      <>
                        <Layout
                          direction="column"
                          align="flex-start"
                          justify="flex-start"
                          shrink
                          className={styles.priceColumn}
                        >
                          <Text level="1">
                            <Price price={devicePrice} noCents />
                          </Text>
                        </Layout>
                        <Layout direction="column" align="flex-start" grow>
                          <Text level="1">
                            <DeviceUsbcName />
                          </Text>
                          <ButterflyIqUsbcFootnote alignLeft />
                        </Layout>
                      </>
                    )}
                    <div>
                      <TooltipDropdownField
                        invalid={false}
                        name="numDevicesUsbC"
                        events={['onChange']}
                        validates={['numDevicesUsbC', 'numCloudUsers']}
                        textField="text"
                        valueField="value"
                        mapFromValue={v => v.value}
                        data={numDevicesUsbCList}
                        as={QuantityDropdownList}
                        isSmall={isMobile}
                      />
                    </div>
                  </Section>
                </>
              }
              <br />
              {mustContactUs ? (
                <Layout pad={4} align="center">
                  <Button href={CONTACT_URL}>
                    <FormattedMessage
                      id="configure.team.step2.contactUs"
                      defaultMessage="Contact us"
                    />
                  </Button>
                  <span style={{ color: '#aaa', marginTop: MARGIN }}>
                    <FormattedMessage
                      defaultMessage="Contact us for orders larger than {maxDevices} probes or {maxCloudUsers} user accounts."
                      values={{ maxDevices, maxCloudUsers }}
                      id="configure.team.contactUsCopy"
                    />
                  </span>
                </Layout>
              ) : (
                <Form.SubmitButton as={Button}>
                  <FormattedMessage
                    id="configure.team.step2.next"
                    defaultMessage="Continue"
                  />
                </Form.SubmitButton>
              )}
            </Form>
          </ProbeImageSegment>
        )}
      </Responsive>
    );
  }
}

ConfigureTeamPageSegment.propTypes = propTypes;

export default withReservation(injectIntl(ConfigureTeamPageSegment));
